import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

function getToken() { return window.__cafeApiToken || localStorage.getItem('cafepos_token'); }
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

function todayStr() { return new Date().toISOString().split('T')[0]; }

const STATUS_LABELS = {
  confirmed: { label: 'Potwierdzona', color: '#185FA5', bg: '#E6F1FB' },
  arrived:   { label: 'Klient przyszedł', color: '#3B6D11', bg: '#EAF3DE' },
  cancelled: { label: 'Anulowana', color: '#767672', bg: '#F0F0EE' },
  no_show:   { label: 'Nie przyszedł', color: '#A32D2D', bg: '#FCEBEB' },
  completed: { label: 'Zakończona', color: '#767672', bg: '#F0F0EE' },
};

export default function Reservations() {
  const [date, setDate]           = useState(todayStr());
  const [reservations, setRes]    = useState([]);
  const [tables, setTables]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null); // null | { mode: 'add'|'edit', reservation? }
  const [toast, setToast]         = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const load = useCallback(async (d) => {
    try {
      const [r, t] = await Promise.all([
        fetch(`/api/reservations?date=${d}`, { headers: authH() }).then(r => r.json()),
        api.getTables(),
      ]);
      setRes(r);
      setTables(t);
    } catch { showToast('Błąd ładowania danych'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { setLoading(true); load(date); }, [date, load]);

  async function handleSave(form) {
    const isEdit = !!form.id;
    const url = isEdit ? `/api/reservations/${form.id}` : '/api/reservations';
    const method = isEdit ? 'PATCH' : 'POST';
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', ...authH() },
      body: JSON.stringify(form),
    });
    const data = await res.json();
    if (!res.ok) { showToast('Błąd: ' + data.error); return; }
    if (data.conflicts) {
      showToast(`⚠ Uwaga: stolik ma już rezerwację o tej porze (${data.conflicts[0].customer_name})`);
    } else {
      showToast(isEdit ? '✓ Zapisano zmiany' : '✓ Dodano rezerwację');
    }
    setModal(null);
    load(date);
  }

  async function setStatus(reservation, status) {
    await fetch(`/api/reservations/${reservation.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authH() },
      body: JSON.stringify({ status }),
    });
    load(date);
  }

  async function handleDelete(reservation) {
    if (!window.confirm(`Usunąć rezerwację „${reservation.customer_name}"?`)) return;
    await fetch(`/api/reservations/${reservation.id}`, { method: 'DELETE', headers: authH() });
    showToast('✓ Usunięto rezerwację');
    load(date);
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Ładowanie...</div>;

  const now = new Date();
  const upcoming = reservations.filter(r => r.status === 'confirmed' && new Date(r.reservation_time) >= now);
  const past     = reservations.filter(r => r.status !== 'confirmed' || new Date(r.reservation_time) < now);

  // Grupowanie po dniach - lista teraz obejmuje wiele dni naprzód, więc dzielimy
  // ją na sekcje z nagłówkiem daty, żeby dało się ogarnąć co jest zaplanowane kiedy
  const grouped = [...upcoming, ...past].reduce((acc, r) => {
    const day = new Date(r.reservation_time).toISOString().split('T')[0];
    (acc[day] = acc[day] || []).push(r);
    return acc;
  }, {});
  const sortedDays = Object.keys(grouped).sort();

  function dayLabel(dayStr) {
    if (dayStr === todayStr()) return 'Dziś';
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    if (dayStr === tomorrow.toISOString().split('T')[0]) return 'Jutro';
    return new Date(dayStr).toLocaleDateString('pl-PL', { weekday: 'long', day: '2-digit', month: '2-digit' });
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '20px 16px' }}>
      {toast && <Toast msg={toast} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 20, fontWeight: 600 }}>📅 Rezerwacje</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ padding: '7px 10px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--text)', fontSize: 13 }} />
          <button className="btn-primary" style={{ width: 'auto', padding: '9px 16px' }}
            onClick={() => setModal({ mode: 'add' })}>
            + Nowa rezerwacja
          </button>
        </div>
      </div>


      {reservations.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40, background: 'var(--surface)', borderRadius: 12, border: '0.5px solid var(--border)' }}>
          Brak rezerwacji od tego dnia
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {sortedDays.map(day => (
            <div key={day}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 8 }}>
                {dayLabel(day)}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {grouped[day].map(r => (
                  <ReservationCard key={r.id} r={r} onEdit={() => setModal({ mode: 'edit', reservation: r })}
                    onStatus={setStatus} onDelete={() => handleDelete(r)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <ReservationModal
          reservation={modal.reservation}
          tables={tables}
          defaultDate={date}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

function ReservationCard({ r, onEdit, onStatus, onDelete }) {
  const st = STATUS_LABELS[r.status] || STATUS_LABELS.confirmed;
  const time = new Date(r.reservation_time).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  const endTime = new Date(new Date(r.reservation_time).getTime() + r.duration_minutes * 60000)
    .toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  const tableName = r.table_name || `Stolik ${r.table_number}`;
  const isPast = r.status === 'cancelled' || r.status === 'no_show' || r.status === 'completed';

  return (
    <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 12, padding: 14, opacity: isPast ? 0.6 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>{time}–{endTime}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{tableName} · {r.party_size} os.</span>
            <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 12, background: st.bg, color: st.color }}>{st.label}</span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, marginTop: 4 }}>{r.customer_name}</div>
          {r.customer_phone && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>📞 {r.customer_phone}</div>}
          {r.notes && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>„{r.notes}"</div>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={onEdit} className="btn-sm">✏️</button>
            <button onClick={onDelete} className="btn-sm danger">🗑</button>
          </div>
          {r.status === 'confirmed' && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => onStatus(r, 'arrived')} className="btn-sm success">Przyszedł</button>
              <button onClick={() => onStatus(r, 'no_show')} className="btn-sm danger">Nie przyszedł</button>
              <button onClick={() => onStatus(r, 'cancelled')} className="btn-sm">Anuluj</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReservationModal({ reservation, tables, defaultDate, onSave, onClose }) {
  const isEdit = !!reservation;
  const [form, setForm] = useState({
    table_id:         reservation?.table_id || (tables[0]?.id || ''),
    customer_name:    reservation?.customer_name || '',
    customer_phone:   reservation?.customer_phone || '',
    party_size:       reservation?.party_size || 2,
    date:             reservation ? new Date(reservation.reservation_time).toISOString().split('T')[0] : defaultDate,
    time:             reservation ? new Date(reservation.reservation_time).toTimeString().slice(0, 5) : '18:00',
    duration_minutes: reservation?.duration_minutes || 90,
    notes:            reservation?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const valid = form.table_id && form.customer_name.trim() && form.date && form.time;

  async function handleSubmit() {
    setSaving(true);
    await onSave({
      id: reservation?.id,
      table_id: form.table_id,
      customer_name: form.customer_name.trim(),
      customer_phone: form.customer_phone.trim() || null,
      party_size: parseInt(form.party_size) || 2,
      reservation_time: `${form.date} ${form.time}:00`,
      duration_minutes: parseInt(form.duration_minutes) || 90,
      notes: form.notes.trim() || null,
    });
    setSaving(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 400, maxWidth: '92vw', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{isEdit ? 'Edytuj rezerwację' : 'Nowa rezerwacja'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
        </div>

        <div className="field">
          <label>Imię i nazwisko klienta *</label>
          <input value={form.customer_name} onChange={e => set('customer_name', e.target.value)} placeholder="np. Jan Kowalski" />
        </div>

        <div className="field">
          <label>Telefon</label>
          <input value={form.customer_phone} onChange={e => set('customer_phone', e.target.value)} placeholder="500 000 000" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
          <div className="field">
            <label>Data *</label>
            <input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
          </div>
          <div className="field">
            <label>Godzina *</label>
            <input type="time" value={form.time} onChange={e => set('time', e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
          <div className="field">
            <label>Liczba osób</label>
            <input type="number" min="1" value={form.party_size} onChange={e => set('party_size', e.target.value)} />
          </div>
          <div className="field">
            <label>Czas trwania (min)</label>
            <input type="number" min="15" step="15" value={form.duration_minutes} onChange={e => set('duration_minutes', e.target.value)} />
          </div>
        </div>

        <div className="field">
          <label>Stolik *</label>
          <select value={form.table_id} onChange={e => set('table_id', e.target.value)}>
            {tables.map(t => (
              <option key={t.id} value={t.id}>{t.name || `Stolik ${t.number}`} ({t.seats} os.)</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>Uwagi</label>
          <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)}
            placeholder="np. urodziny, alergia na orzechy..."
            style={{ width: '100%', padding: '9px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box', resize: 'vertical' }} />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: '11px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-muted)' }}>
            Anuluj
          </button>
          <button onClick={handleSubmit} disabled={!valid || saving}
            style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
              background: valid ? 'var(--blue)' : 'var(--border)',
              color: valid ? '#fff' : 'var(--text-muted)',
              cursor: valid ? 'pointer' : 'not-allowed' }}>
            {saving ? 'Zapisuję...' : (isEdit ? 'Zapisz zmiany' : 'Dodaj rezerwację')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Toast({ msg }) {
  return (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#1a1a1a', color: '#fff', padding: '10px 20px', borderRadius: 8, fontSize: 13, zIndex: 999 }}>
      {msg}
    </div>
  );
}
